import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, CreditCard, Calendar, Package, AlertCircle, XCircle, RefreshCw, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    fetchSubscription();
    fetchPlans();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/my-subscription`);
      setSubscription(response.data);
      
      if (response.data?.subscription?.plan_id) {
        const planResponse = await axios.get(`${API}/subscriptions/plans`);
        const plan = planResponse.data.find(p => p.id === response.data.subscription.plan_id);
        setCurrentPlan(plan);
      }
    } catch (error) {
      console.error('Failed to fetch subscription', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      setAllPlans(response.data || []);
    } catch (error) {
      console.error('Failed to fetch plans');
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/subscriptions/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Ihr Abonnement wurde zum Ende der Laufzeit gekündigt');
      setShowCancelConfirm(false);
      fetchSubscription();
    } catch (error) {
      console.error('Cancel failed:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Kündigen');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/subscriptions/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Ihr Abonnement wurde reaktiviert!');
      fetchSubscription();
    } catch (error) {
      console.error('Reactivate failed:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Reaktivieren');
    } finally {
      setReactivating(false);
    }
  };

  const getSubscriptionStatus = () => {
    if (!subscription?.subscription) return 'none';
    const sub = subscription.subscription;
    
    if (sub.cancel_at_period_end) return 'cancelling';
    if (sub.status === 'past_due') return 'past_due';
    if (sub.status === 'unpaid') return 'unpaid';
    if (sub.status === 'canceled' || sub.status === 'expired') return 'expired';
    return 'active';
  };

  const getSessionsRemaining = () => {
    if (!subscription?.subscription) return 0;
    const used = subscription.subscription.sessions_used_this_period || 0;
    const total = subscription.subscription.sessions_per_month || 0;
    return Math.max(0, total - used);
  };

  const getPeriodEnd = () => {
    if (!subscription?.subscription?.current_period_end) {
      return 'Unbegrenzt';
    }
    return new Date(subscription.subscription.current_period_end).toLocaleDateString('de-DE');
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <div className="flex items-center justify-center h-64">
          <p style={{ color: '#94A3B8' }}>Laden...</p>
        </div>
      </div>
    );
  }

  if (!subscription || !currentPlan) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <div className="max-w-4xl mx-auto">
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#F59E0B' }} />
              <h3 className="text-xl font-bold mb-2" style={{ color: '#E6EDF3' }}>
                Kein aktives Abonnement
              </h3>
              <p className="mb-6" style={{ color: '#94A3B8' }}>
                Sie haben noch kein Abonnement. Wählen Sie einen Plan, um zu starten.
              </p>
              <Button
                onClick={() => navigate('/signup')}
                style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
              >
                Plan auswählen
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold mb-8" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
          Mein Abonnement
        </h2>

        {/* Cancellation Warning Banner */}
        {getSubscriptionStatus() === 'cancelling' && (
          <div 
            className="mb-6 p-4 rounded-lg border-2 flex items-center justify-between"
            style={{ backgroundColor: '#F59E0B15', borderColor: '#F59E0B' }}
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6" style={{ color: '#F59E0B' }} />
              <div>
                <p className="font-semibold" style={{ color: '#F59E0B' }}>
                  Kündigung zum {getPeriodEnd()} aktiv
                </p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  Ihr Abo läuft zum Ende der Laufzeit aus. Bis dahin haben Sie vollen Zugriff.
                </p>
              </div>
            </div>
            <Button
              onClick={handleReactivateSubscription}
              disabled={reactivating}
              style={{ backgroundColor: '#22C55E', color: '#fff' }}
            >
              {reactivating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Abo reaktivieren
            </Button>
          </div>
        )}

        {/* Past Due Warning */}
        {getSubscriptionStatus() === 'past_due' && (
          <div 
            className="mb-6 p-4 rounded-lg border-2"
            style={{ backgroundColor: '#EF444415', borderColor: '#EF4444' }}
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6" style={{ color: '#EF4444' }} />
              <div>
                <p className="font-semibold" style={{ color: '#EF4444' }}>
                  ⚠️ Zahlung ausstehend
                </p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  Ihre letzte Zahlung ist fehlgeschlagen. Bitte aktualisieren Sie Ihre Zahlungsmethode innerhalb von 2 Tagen.
                </p>
              </div>
            </div>
            <Button
              className="mt-3"
              onClick={() => window.open(`${BACKEND_URL}/api/checkout/customer-portal`, '_blank')}
              style={{ backgroundColor: '#EF4444', color: '#fff' }}
            >
              Zahlungsmethode aktualisieren
            </Button>
          </div>
        )}

        {/* Expired Warning */}
        {getSubscriptionStatus() === 'expired' && (
          <div 
            className="mb-6 p-4 rounded-lg border-2"
            style={{ backgroundColor: '#EF444415', borderColor: '#EF4444' }}
          >
            <div className="flex items-center gap-3">
              <XCircle className="w-6 h-6" style={{ color: '#EF4444' }} />
              <div>
                <p className="font-semibold" style={{ color: '#EF4444' }}>
                  Abonnement abgelaufen
                </p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  Ihr Abo ist abgelaufen. Einige Funktionen sind eingeschränkt.
                </p>
              </div>
            </div>
            <Button
              className="mt-3"
              onClick={() => navigate('/signup')}
              style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
            >
              Neues Abo abschließen
            </Button>
          </div>
        )}

        {/* Current Plan Overview */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1EA0E6', borderWidth: '2px' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                <Package className="w-5 h-5" style={{ color: '#7CD4FD' }} />
                Aktueller Plan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold mb-2" style={{ color: '#7CD4FD' }}>
                {currentPlan.name}
              </p>
              <p style={{ color: '#94A3B8' }}>
                {currentPlan.currency === 'CHF' ? 'CHF ' : currentPlan.currency === 'USD' ? '$' : '€'}
                {currentPlan.price} / {currentPlan.interval === 'month' ? 'Monat' : currentPlan.interval === 'year' ? 'Jahr' : 'einmalig'}
              </p>
            </CardContent>
          </Card>

          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                <CreditCard className="w-5 h-5" style={{ color: '#7CD4FD' }} />
                Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold mb-2" style={{ color: '#22C55E' }}>
                {getSessionsRemaining()}
              </p>
              <p style={{ color: '#94A3B8' }}>
                von {subscription.subscription.sessions_per_month} übrig
              </p>
            </CardContent>
          </Card>

          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                <Calendar className="w-5 h-5" style={{ color: '#7CD4FD' }} />
                Gültigkeit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-bold mb-2" style={{ color: '#E6EDF3' }}>
                {getPeriodEnd()}
              </p>
              <p style={{ 
                color: getSubscriptionStatus() === 'active' ? '#22C55E' : 
                       getSubscriptionStatus() === 'cancelling' ? '#F59E0B' : '#EF4444'
              }}>
                {getSubscriptionStatus() === 'active' ? '✓ Aktiv' : 
                 getSubscriptionStatus() === 'cancelling' ? '⏳ Kündigung aktiv' :
                 getSubscriptionStatus() === 'past_due' ? '⚠️ Zahlung ausstehend' :
                 '❌ Abgelaufen'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Plan Details */}
        <Card className="mb-8" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>Plan-Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-4 text-lg" style={{ color: '#7CD4FD' }}>Enthaltene Features:</h4>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                    <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                    <span className="font-semibold">
                      {subscription.subscription.sessions_per_month === 0 || !subscription.subscription.sessions_per_month
                        ? 'Unbegrenzte Diagnosen pro Monat' 
                        : `${subscription.subscription.sessions_per_month} Diagnosen pro Monat`}
                    </span>
                  </li>
                  
                  {/* Basic Features */}
                  {currentPlan.features?.basic_vehicle_database && (
                    <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                      Basis Fahrzeug-Datenbank
                    </li>
                  )}
                  {currentPlan.features?.pdf_reports && (
                    <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                      PDF Reports
                    </li>
                  )}
                  {currentPlan.features?.email_support && (
                    <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                      Email Support
                    </li>
                  )}
                  {currentPlan.features?.detailed_test_instructions && (
                    <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                      Detaillierte Prüfanweisungen
                    </li>
                  )}
                  
                  {/* Advanced Features */}
                  {currentPlan.features?.extended_vehicle_database && (
                    <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                      Erweiterte Fahrzeugdatenbank
                    </li>
                  )}
                  {currentPlan.features?.dual_ai_system && (
                    <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#7CD4FD' }} />
                      <span className="font-semibold">Dual-AI-System (Gemini + GPT-4o)</span>
                    </li>
                  )}
                  {currentPlan.features?.custom_knowledge_base && (
                    <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                      Eigene Knowledge Base
                    </li>
                  )}
                  {currentPlan.features?.live_web_research && (
                    <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                      Live Web-Recherche
                    </li>
                  )}
                  {currentPlan.features?.priority_support && (
                    <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                      Prioritäts-Support
                    </li>
                  )}
                  {currentPlan.features?.custom_branding && (
                    <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                      Custom Branding
                    </li>
                  )}
                  
                  {/* Diagnosis Features */}
                  {currentPlan.features?.actual_value_assessment && (
                    <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#A78BFA' }} />
                      <span className="font-semibold">Istwert-Prüfung & Beurteilung</span>
                    </li>
                  )}
                  {currentPlan.features?.repair_instructions && (
                    <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                      Reparatur-Anleitungen
                    </li>
                  )}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-4 text-lg" style={{ color: '#7CD4FD' }}>Nutzungsstatistik:</h4>
                <div className="space-y-2">
                  <div className="flex justify-between" style={{ color: '#E6EDF3' }}>
                    <span>Genutzte Sessions:</span>
                    <span>{subscription.subscription.sessions_used_this_period || 0}</span>
                  </div>
                  <div className="flex justify-between" style={{ color: '#E6EDF3' }}>
                    <span>Verbleibend:</span>
                    <span>{getSessionsRemaining()}</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${(subscription.subscription.sessions_used_this_period / subscription.subscription.sessions_per_month) * 100}%`,
                        backgroundColor: '#1EA0E6'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upgrade Options */}
        {allPlans.length > 0 && (
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardHeader>
              <CardTitle style={{ color: '#E6EDF3' }}>Andere verfügbare Pläne</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {allPlans
                  .filter(plan => plan.id !== currentPlan.id)
                  .map(plan => (
                    <div
                      key={plan.id}
                      className="p-4 rounded-lg"
                      style={{ backgroundColor: '#0B0F14', border: '1px solid #1F2937' }}
                    >
                      <h4 className="font-bold mb-2" style={{ color: '#E6EDF3' }}>{plan.name}</h4>
                      <p className="text-xl font-bold mb-2" style={{ color: '#7CD4FD' }}>
                        {plan.currency === 'CHF' ? 'CHF ' : plan.currency === 'USD' ? '$' : '€'}
                        {plan.price}
                      </p>
                      <p className="text-sm mb-3" style={{ color: '#94A3B8' }}>
                        {plan.limits?.sessions_per_month} Diagnosen / 
                        {plan.interval === 'month' ? 'Monat' : plan.interval === 'year' ? 'Jahr' : 'einmalig'}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => toast.info('Kontaktieren Sie den Support für ein Upgrade')}
                        style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
                      >
                        Mehr erfahren
                      </Button>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cancel Subscription Section - Only shown for recurring subscriptions */}
        {getSubscriptionStatus() === 'active' && subscription?.subscription?.stripe_subscription_id && (
          <Card className="mt-8" style={{ backgroundColor: '#0F141A', borderColor: '#374151' }}>
            <CardHeader>
              <CardTitle style={{ color: '#94A3B8' }}>Abonnement verwalten</CardTitle>
            </CardHeader>
            <CardContent>
              {!showCancelConfirm ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p style={{ color: '#94A3B8' }}>
                      Sie können Ihr Abonnement jederzeit zum Ende der aktuellen Laufzeit kündigen.
                    </p>
                    <p className="text-sm mt-1" style={{ color: '#6B7280' }}>
                      Bis zum Ablauf haben Sie weiterhin vollen Zugriff auf alle Funktionen.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowCancelConfirm(true)}
                    style={{ borderColor: '#EF4444', color: '#EF4444' }}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Abo kündigen
                  </Button>
                </div>
              ) : (
                <div className="p-4 rounded-lg border-2" style={{ backgroundColor: '#EF444410', borderColor: '#EF4444' }}>
                  <div className="flex items-start gap-3 mb-4">
                    <AlertCircle className="w-6 h-6 mt-1" style={{ color: '#EF4444' }} />
                    <div>
                      <p className="font-semibold" style={{ color: '#EF4444' }}>
                        Sind Sie sicher?
                      </p>
                      <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
                        Ihr Abonnement wird zum <strong>{getPeriodEnd()}</strong> beendet.
                        Danach verlieren Sie Zugriff auf:
                      </p>
                      <ul className="text-sm mt-2 space-y-1" style={{ color: '#94A3B8' }}>
                        <li>❌ Neue Diagnosen erstellen</li>
                        <li>❌ Technische Fahrzeugdaten</li>
                        <li>❌ Neue Fahrzeuge anlegen</li>
                        <li className="text-green-400">✓ Bisherige Diagnosen bleiben sichtbar</li>
                      </ul>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                      style={{ backgroundColor: '#EF4444', color: '#fff' }}
                    >
                      {cancelling ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      Ja, zum {getPeriodEnd()} kündigen
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowCancelConfirm(false)}
                      style={{ borderColor: '#374151', color: '#E6EDF3' }}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Info for one-time purchases (no cancel button) */}
        {getSubscriptionStatus() === 'active' && !subscription?.subscription?.stripe_subscription_id && (
          <Card className="mt-8" style={{ backgroundColor: '#0F141A', borderColor: '#374151' }}>
            <CardHeader>
              <CardTitle style={{ color: '#94A3B8' }}>Einmalkauf</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Check className="w-6 h-6" style={{ color: '#22C55E' }} />
                <div>
                  <p style={{ color: '#E6EDF3' }}>
                    Sie haben einen Einmalkauf getätigt.
                  </p>
                  <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
                    Dieser Plan läuft nicht automatisch ab und muss nicht gekündigt werden. 
                    Bei einem Einmalkauf haben Sie dauerhaften Zugang gemäß den Planbedingungen.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};